import { Maybe, Undef } from 'types/UtilityTypes';

import { LTLItem, PACKAGE_CODE_TO_TYPE, QuoteFormStop } from '../../types';
import { BatchLTLShipmentInput } from '../types';

const PACKAGING_PLURALS: Record<string, string> = {
  box: 'boxes',
  pallet: 'pallets',
  skid: 'skids',
  crate: 'crates',
  piece: 'pieces',
  carton: 'cartons',
  drum: 'drums',
};

// e.g. "1 box 39×18×75 in, 71 lb, class 125"
export function getFreightSummary(
  items: LTLItem[],
  fallbackFreightClass: Undef<string>
): string {
  return items
    .map((item) => {
      const count = item.handlingUnits ?? item.pieces;
      const handling = count
        ? `${count} ${getPackagingNoun(item.packageType, count)}`
        : '';
      const dims =
        item.length && item.width && item.height
          ? `${item.length}×${item.width}×${item.height} in`
          : '';
      const freightClass = item.freightClass || fallbackFreightClass;

      return [
        [handling, dims].filter(Boolean).join(' '),
        item.weight ? `${item.weight.toLocaleString('en-US')} lb` : '',
        freightClass ? `class ${freightClass}` : '',
      ]
        .filter(Boolean)
        .join(', ');
    })
    .filter(Boolean)
    .join('; ');
}

// "Phoenix, AZ 85009", falling back to whatever the rep typed.
export function getStopFullLabel(stop: Undef<QuoteFormStop>): string {
  if (!stop) {
    return '';
  }

  const cityState = [stop.city, stop.state].filter(Boolean).join(', ');
  const label = [cityState, stop.zip].filter(Boolean).join(' ');

  return label || stop.location?.trim() || '';
}

// The ZIP alone when known; the origin is the same few warehouses, so the ZIP is enough.
export function getStopZipLabel(stop: Undef<QuoteFormStop>): string {
  return stop?.zip?.trim() || getStopFullLabel(stop);
}

// The origin every shipment shares, or null when they differ or one is missing.
export function getSharedOriginLabel(
  shipments: BatchLTLShipmentInput[]
): Maybe<string> {
  const labels = new Set(
    shipments.map((shipment) => getStopFullLabel(shipment.stops[0]))
  );
  const [label] = [...labels];

  return shipments.length > 1 && labels.size === 1 && label ? label : null;
}

function getPackagingNoun(packageType: Undef<number>, count: number): string {
  const typeName = packageType ? PACKAGE_CODE_TO_TYPE[packageType] : undefined;
  const noun = (typeName ?? 'Pallet').toLowerCase();

  if (count === 1) {
    return noun;
  }

  return PACKAGING_PLURALS[noun] ?? noun;
}
